<template>
  <div class="items-container">
    <base-loader v-if="dataIsLoading" />

    <template v-else>
      <base-nav
        title="Характеристики груза"
        :show-back="true"
        back-fallback="Home"
        :back-fallback-pattern="/\/cargo_properties/"
      >
        <base-primary-button
          class="nav-btn"
          @click="toggleCreateForm"
        >
          {{ showCreateForm ? "Отмена" : "Создать" }}
        </base-primary-button>
      </base-nav>

      <transition name="fade-slide">
        <div v-if="showCreateForm" class="card item-form-wrap">
          <h4 class="form-title">Создание характеристики груза</h4>
          <CargoPropertyForm
            :is-loading="isLoading"
            @save="handleCreate"
            @cancel="showCreateForm = false"
          />
        </div>
      </transition>

      <div v-if="localItems.length === 0" class="no-data">
        Реестр характеристик груза пуст
      </div>

      <ul v-else class="items-list">
        <BaseSettingsListItemCard
          v-for="item in localItems"
          :key="item.id"
          :enabled="item.enabled"
          :is-editing="editingItemId === item.id"
          :is-loading="isLoading"
          :created-at="item.created_at"
          :updated-at="item.updated_at"
          edit-title="Редактирование характеристики груза"
          @edit="startEdit(item)"
          @toggle="toggleItem(item)"
        >
          <template #edit>
            <CargoPropertyForm
              :initial-data="item"
              :is-loading="isLoading"
              @save="(payload) => handleUpdate(item.id, payload)"
              @cancel="editingItemId = null"
            />
          </template>

          <div class="item-top">
            <span class="item-title">
              {{ item.title || "—" }}
            </span>
            <span class="item-code">
              {{ item.code || "—" }}
            </span>
          </div>
        </BaseSettingsListItemCard>
      </ul>

      <pagination-component
        v-if="pagination.pages > 1 && !dataIsLoading"
        :page="pagination.page"
        :pages="pagination.pages"
        :series="pagination.series"
      />
    </template>
  </div>
</template>

<script>
import { mapState, mapActions } from "pinia";
import { useCargoPropertyStore } from "@/stores/cargoPropertyStore";
import { useNotificationStore } from "@/stores/notificationStore";

import CargoPropertyForm from "@/components/Cargo/CargoPropertyForm.vue";
import PaginationComponent from "@/components/PaginationComponent.vue";

export default {
  name: "CargoPropertiesPage",

  components: {
    CargoPropertyForm,
    PaginationComponent,
  },

  data() {
    return {
      dataIsLoading: true,
      showCreateForm: false,
      editingItemId: null,
      localItems: [],
    };
  },

  computed: {
    ...mapState(useCargoPropertyStore, [
      "items",
      "isLoading",
      "pagination",
    ]),
    notificationStore() {
      return useNotificationStore();
    },
  },

  watch: {
    "$route.query.page": {
      immediate: false,
      handler(newPage) {
        this.loadItems(newPage);
      },
    },
  },

  methods: {
    ...mapActions(useCargoPropertyStore, [
      "getItemsRequest",
      "createItem",
      "updateItem",
      "itemEnable",
      "itemDisable",
    ]),

    async loadItems(page) {
      const pageNumber =
        parseInt(page) || parseInt(this.$route.query.page) || 1;

      try {
        this.dataIsLoading = true;
        await this.getItemsRequest({ page: pageNumber });
        this.localItems = (this.items || []).filter(Boolean);
      } finally {
        this.dataIsLoading = false;
      }
    },

    updateLocalItem(updatedItem) {
      const index = this.localItems.findIndex(
        (i) => i && i.id === updatedItem.id
      );
      if (index !== -1) {
        this.localItems.splice(index, 1, updatedItem);
      }
    },

    toggleCreateForm() {
      this.showCreateForm = !this.showCreateForm;
      if (this.showCreateForm) {
        this.editingItemId = null;
      }
    },

    startEdit(item) {
      this.editingItemId = item.id;
      this.showCreateForm = false;
    },

    async handleCreate(payload) {
      try {
        await this.createItem(payload);
        this.localItems = (this.items || []).filter(Boolean);
        this.showCreateForm = false;
        this.notificationStore.showSuccess(
          "Характеристика груза создана"
        );
      } catch {
        // ошибка обрабатывается в сторе
      }
    },

    async handleUpdate(id, payload) {
      try {
        await this.updateItem(id, payload);
        this.localItems = (this.items || []).filter(Boolean);
        this.editingItemId = null;
        this.notificationStore.showSuccess(
          "Характеристика груза обновлена"
        );
      } catch {
        // ошибка обрабатывается в сторе
      }
    },

    async toggleItem(item) {
      try {
        if (item.enabled) {
          await this.itemDisable(item.id);
          this.updateLocalItem({ ...item, enabled: false });
          this.notificationStore.showSuccess(
            "Характеристика груза отключена"
          );
        } else {
          await this.itemEnable(item.id);
          this.updateLocalItem({ ...item, enabled: true });
          this.notificationStore.showSuccess(
            "Характеристика груза включена"
          );
        }
      } catch {
        // ошибка обрабатывается в сторе
      }
    },
  },

  async created() {
    await this.loadItems(this.$route.query.page);
  },
};
</script>

<style scoped>
.items-container {
  flex-grow: 1;
}

.item-form-wrap {
  margin-bottom: var(--base-out-indent);
}

.form-title {
  margin-bottom: var(--base-in-indent);
}

.items-list {
  display: flex;
  flex-direction: column;
  gap: var(--base-out-indent);
  margin-bottom: 15px;
}

.item-top {
  display: flex;
  align-items: baseline;
  gap: 10px;
  flex-wrap: wrap;
}

.item-title {
  font-weight: 600;
  font-size: 17px;
}

.item-code {
  color: #666;
}
</style>
